import React, {useEffect, useMemo, useState} from "react";
import {Button, CircularProgress, Typography} from "@mui/material";
import {Info, UserForm, UserPasswordForm} from "@atttomyx/react-components";
import {useQuery} from "@atttomyx/react-hooks";
import {authService, stripeService} from "../../services";
import {errors, forms, validation} from "@atttomyx/shared-utils";
import "./thankYouPage.css";

const toData = (user) => {
    return {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        valid: user.firstName && user.lastName && user.email && user.phone
            && validation.validEmail(user.email)
            && validation.validPhone(user.phone),
    }
};

const freshPassword = () => {
    return {
        password: null,
        confirm: null,
        valid: false,
    }
};

const MSG_PAYMENT = "Unable to confirm payment. Please contact Support.";
const MSG_EMAIL = "Email address already in use. Please login normally.";

const ThankYouPage = (props) => {
    const { snackbar, onBackToLogin, onLogin } = props;
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState(toData({}));
    const [password, setPassword] = useState(freshPassword());
    const query = useQuery();

    const sessionId = useMemo(() => {
        return query.get("session_id");
    }, [query]);

    useEffect(() => {
        const failure = () => {
            snackbar.setError(MSG_PAYMENT);
            setLoading(false);
        };

        const success = (json) => {
            setData(toData(json));
            setLoading(false);
        };

        setTimeout(() => document.body.classList.add("outside-arena"), 1);
        stripeService.resolve(sessionId, success, failure);

        return () => {
            document.body.classList.remove("outside-arena");
        }
    }, []);

    const submit = () => {
        const user = {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone,
            password: password.password,
        };

        const failure1 = (err) => {
            const code = errors.getStatusCode(err);

            if (code === 400) {
                success1();

            } else {
                snackbar.setError(MSG_PAYMENT);
                setSaving(false);
            }
        };

        const failure2 = () => {
            snackbar.setWarning(MSG_EMAIL);
            onBackToLogin();
        };

        const success2 = () => {
            onLogin();
        };

        const success1 = () => {
            authService.login(user.email, user.password, null, success2, failure2);
        };

        setSaving(true);
        stripeService.fulfill(sessionId, user, success1, failure1);
    };

    const valid = data.valid && password.valid;
    const modified = forms.differ(toData({}), data)
        || forms.differ(freshPassword(), password);

    return <div className="thankYouPage">
        <Typography variant="h6" paragraph={true}>
            Thank you for investing in your future!
        </Typography>
        {loading ?
            <CircularProgress size="24px"/> :
            <>
                <Typography variant="body1">
                    Welcome to the portal.
                </Typography>
                <Typography variant="caption" paragraph={true}>
                    Please remember your email and password.
                </Typography>
                <UserForm
                    snackbar={snackbar}
                    data={data}
                    onChange={setData}
                    showImage={false}
                    showPhone={true}
                    showBirthday={false}
                    requirePhone={true}
                />
                <UserPasswordForm
                    data={password}
                    onChange={setPassword}
                />
                <Info>
                    Email is used to login, please use one that you can easily remember.
                </Info>
                <div className="action">
                    {saving ?
                        <CircularProgress size="24px"/> :
                        <Button
                            color="primary"
                            size="large"
                            disabled={!valid || !modified}
                            onClick={submit}
                        >
                            Enter the arena
                        </Button>}
                </div>
            </>}
        <div className="action">
            <Button
                color="secondary"
                variant="text"
                onClick={onBackToLogin}
            >
                Back to login
            </Button>
        </div>
    </div>
};

export default ThankYouPage;
